<template>
  <Input
    v-bind="$attrs"
    ref="input"
    tag="input"
    :model-value="inputValue"
    :error="error"
    :aria-label="ariaLabel"
    :max="max"
    :name="name"
    :modifiers="allModifiers"
    :disabled="disabled"
    :clearable="clearable"
    :test-id="testId"
    :is-required="isRequired"
    @update:model-value="(val) => setValue(val, false)"
    @blur="(evt) => handleBlur(evt, true)"
    @click="() => emit('click')"
  >
    <template v-for="(slot, index) of slotNames" :key="index" #[slot]>
      <slot :name="slot" />
    </template>
  </Input>
</template>

<script setup lang="ts">
  import { computed, ref } from "vue";

  const slots = useSlots();
  const slotNames = Object.keys(slots) as "default"[];
  const _modelValue = defineModel<string>();

  const props = withDefaults(
    defineProps<{
      active?: boolean;
      ariaLabel?: string;
      clearable?: boolean;
      disabled?: boolean;
      error?: string;
      max?: number;
      min?: number;
      modifiers?: string[];
      multi?: boolean;
      name?: string;
      selected?: boolean;
      testId?: string;
      isRequired?: boolean;
    }>(),
    {
      active: false,
      ariaLabel: undefined,
      clearable: false,
      disabled: false,
      error: undefined,
      max: undefined,
      min: undefined,
      modifiers: () => [],
      multi: false,
      name: "",
      selected: false,
      testId: undefined,
      isRequired: false,
    }
  );

  const {
    value: inputValue,
    errorMessage,
    handleBlur,
    setValue,
  } = useField(() => props.name, undefined, {
    syncVModel: props.name ? false : true,
    validateOnMount: false,
    controlled: !!props.name,
  });

  const error = computed(() => {
    return props.error || errorMessage.value;
  });

  const input = ref<HTMLElement | null>(null);

  const el = computed(() => {
    return input.value?.input;
  });
  const wrapper = computed(() => {
    return input.value?.wrapper;
  });

  const allModifiers = computed(() => {
    const m = [...props.modifiers];
    if (props.multi) m.push("multi");

    return m;
  });

  const emit = defineEmits(["click"]);

  defineExpose({
    el,
    wrapper,
    setValue,
    inputValue,
  });
</script>
